import { createContext, useContext, useState, useCallback } from "react";

// Spanish is the source language: the Spanish string is the key.
const EN = {
  // ---- Layout / nav / footer ----
  "Mi panel": "My dashboard",
  "Nueva solicitud": "New request",
  "Perfil": "Profile",
  "Administración": "Admin",
  "Cómo funciona": "How it works",
  "Tarifas": "Pricing",
  "Entrar": "Log in",
  "Crear cuenta": "Create account",
  "Salir": "Log out",
  "Cerrar sesión": "Log out",
  "Bróker licenciado de Copart. Pujamos por ti.": "Licensed Copart broker. We bid for you.",
  "Depósitos 100% reembolsables si no ganamos tu subasta (aplica cuota de procesamiento de tarjeta divulgada). CarBid opera como bróker: las pujas se ejecutan manualmente por nuestro equipo en nuestra cuenta de Copart.":
    "Deposits are 100% refundable if we don't win your auction (disclosed card processing fee applies). CarBid operates as a broker: bids are placed manually by our team from our own Copart account.",
  "Contacto": "Contact",
  "Pagos seguros con Stripe": "Secure payments with Stripe",

  // ---- StatusTracker ----
  "Enviada": "Submitted",
  "Depósito": "Deposit",
  "Pujando": "Bidding",
  "Resultado": "Result",
  "Pago saldo": "Balance due",
  "Pagado": "Paid",
  "En tránsito": "In transit",
  "Entregado": "Delivered",
  "Ganada": "Won",
  "Perdida": "Lost",
  "Subasta perdida — tu depósito será reembolsado (menos la cuota de procesamiento divulgada).":
    "Auction lost — your deposit will be refunded (minus the disclosed processing fee).",

  // ---- Login ----
  "Iniciar sesión": "Log in",
  "Accede a tu panel de solicitudes CarBid.": "Access your CarBid requests dashboard.",
  "Correo electrónico": "Email",
  "Contraseña": "Password",
  "Entrando...": "Logging in...",
  "¿No tienes cuenta?": "Don't have an account?",
  "Regístrate": "Sign up",
  "Bienvenido": "Welcome",

  // ---- Register ----
  "Después del registro deberás subir tu ID (licencia o pasaporte), requisito de Copart.":
    "After registering you'll need to upload your ID (driver's license or passport), a Copart requirement.",
  "Nombre completo": "Full name",
  "Teléfono / WhatsApp": "Phone / WhatsApp",
  "Contraseña (mín. 6 caracteres)": "Password (min. 6 characters)",
  "Creando...": "Creating...",
  "¿Ya tienes cuenta?": "Already have an account?",
  "Cuenta creada. Ahora sube tu ID desde tu perfil.": "Account created. Now upload your ID from your profile.",
  "Error inesperado. Intenta de nuevo.": "Unexpected error. Please try again.",

  // ---- PaymentResult ----
  "Confirmando tu pago...": "Confirming your payment...",
  "Estamos verificando tu depósito con Stripe. No cierres esta página.":
    "We're verifying your deposit with Stripe. Don't close this page.",
  "¡Depósito confirmado!": "Deposit confirmed!",
  "Tu solicitud pasó a \"Depósito pagado\". Nuestro equipo comenzará a preparar tu puja.":
    "Your request is now \"Deposit paid\". Our team will start preparing your bid.",
  "Ver mi solicitud": "View my request",
  "Pago en verificación": "Payment being verified",
  "El pago está siendo procesado. Revisa tu panel en unos minutos.":
    "Your payment is being processed. Check your dashboard in a few minutes.",
  "Ir a mi panel": "Go to my dashboard",
  "Pago cancelado": "Payment cancelled",
  "No se realizó ningún cargo. Puedes reintentar el pago del depósito desde tu solicitud.":
    "No charge was made. You can retry the deposit payment from your request.",
  "Volver a mi panel": "Back to my dashboard",

  // ---- Profile ----
  "Mi perfil": "My profile",
  "Guardar cambios": "Save changes",
  "Guardando...": "Saving...",
  "Perfil actualizado": "Profile updated",
  "Documento de identidad": "ID document",
  "Copart exige una identificación oficial (licencia de conducir o pasaporte) para poder pujar a tu nombre. Imagen o PDF, máx. 5MB.":
    "Copart requires an official ID (driver's license or passport) so we can bid on your behalf. Image or PDF, max 5MB.",
  "Documento en archivo": "Document on file",
  "Ver documento": "View document",
  "Pendiente — súbelo para que podamos pujar por ti": "Pending — upload it so we can bid for you",
  "Subir documento": "Upload document",
  "Subiendo...": "Uploading...",
  "Documento de identidad subido correctamente": "ID document uploaded successfully",

  // ---- Dashboard ----
  "Rastrea tus solicitudes de puja en Copart.": "Track your Copart bid requests.",
  "Solicitudes": "Requests",
  "Activas": "Active",
  "Ganadas": "Won",
  "Cargando solicitudes...": "Loading requests...",
  "Aún no tienes solicitudes": "No requests yet",
  "Envía tu primer lote de Copart y autoriza tu puja máxima.": "Submit your first Copart lot and authorize your max bid.",
  "Enviar un lote": "Submit a lot",
  "Puja máx:": "Max bid:",
  "Estimador de costos": "Cost estimator",
  "Precio estimado del vehículo (USD)": "Estimated vehicle price (USD)",
  "Vehículo": "Vehicle",
  "Comisión de bróker": "Broker fee",
  "Impuestos estimados (6%)": "Estimated taxes (6%)",
  "Transporte estimado": "Estimated transport",
  "Total estimado": "Estimated total",
  "Estimación referencial. No incluye tarifas del patio de Copart ni reparaciones.":
    "Reference estimate. Does not include Copart yard fees or repairs.",

  // ---- NewBid ----
  "Nueva solicitud de puja": "New bid request",
  "Datos del lote": "Lot details",
  "Puja máxima y depósito": "Max bid & deposit",
  "Confirmación": "Confirmation",
  "Paso {step} de 3": "Step {step} of 3",
  "URL o número de lote de Copart": "Copart lot URL or number",
  "Buscar": "Search",
  "Buscando...": "Searching...",
  "Búsqueda automática no disponible — esto es normal, Copart bloquea el acceso automatizado. Completa los datos manualmente; nuestro equipo los verificará.":
    "Auto-lookup unavailable — this is normal, Copart blocks automated access. Fill in the details manually; our team will verify them.",
  "Datos cargados automáticamente. Verifícalos abajo.": "Details loaded automatically. Please review them below.",
  "Datos del lote cargados. Revísalos antes de continuar.": "Lot details loaded. Review them before continuing.",
  "Búsqueda automática no disponible. Ingresa los datos manualmente.":
    "Auto-lookup unavailable. Enter the details manually.",
  "Datos del vehículo": "Vehicle details",
  "Número de lote": "Lot number",
  "Fecha de subasta": "Auction date",
  "Año": "Year",
  "Marca": "Make",
  "Modelo": "Model",
  "Millaje": "Mileage",
  "Tipo de título": "Title type",
  "Seleccionar": "Select",
  "Otro / No sé": "Other / Not sure",
  "Ubicación (patio Copart)": "Location (Copart yard)",
  "URL de imagen (opcional)": "Image URL (optional)",
  "Continuar": "Continue",
  "Puja máxima autorizada (USD)": "Authorized max bid (USD)",
  "Nuestro equipo nunca pujará por encima de este monto.": "Our team will never bid above this amount.",
  "Comisión de bróker (solo si ganamos)": "Broker fee (only if we win)",
  "Atrás": "Back",
  "Revisar": "Review",
  "Resumen de tu solicitud": "Your request summary",
  "Lote": "Lot",
  "Puja máxima": "Max bid",
  "Comisión (si ganamos)": "Broker fee (if we win)",
  "Depósito a pagar ahora": "Deposit due now",
  "Por confirmar": "To be confirmed",
  "Procesando...": "Processing...",
  "Pagar depósito {amount}": "Pay {amount} deposit",
  "Pago seguro procesado por Stripe. El depósito es reembolsable si no ganamos tu subasta.":
    "Secure payment processed by Stripe. The deposit is refundable if we don't win your auction.",
  "Solicitud creada. Ahora paga tu depósito reembolsable.": "Request created. Now pay your refundable deposit.",

  // ---- BidDetail ----
  "Volver al panel": "Back to dashboard",
  "Ver en Copart": "View on Copart",
  "Cargando...": "Loading...",
  "Detalles": "Details",
  "Título": "Title",
  "Ubicación": "Location",
  "Puja máxima autorizada": "Authorized max bid",
  "Precio final": "Final price",
  "Saldo por pagar": "Balance due",
  "Paga tu depósito para activar la puja": "Pay your deposit to activate your bid",
  "Nuestro equipo no puede pujar hasta que el depósito de {amount} esté confirmado. Si la fecha de subasta pasa antes de que se acredite, la solicitud se cancela y te avisamos.":
    "Our team cannot bid until your {amount} deposit is confirmed. If the auction date passes before it clears, the request is cancelled and we'll notify you.",
  "Redirigiendo...": "Redirecting...",
  "Pagar {amount} con tarjeta": "Pay {amount} by card",
  "¡Ganamos tu subasta!": "We won your auction!",
  "Precio final: {price}. Saldo por pagar: {balance} (precio + comisión − depósito).":
    "Final price: {price}. Balance due: {balance} (price + fee − deposit).",
  "Instrucciones de pago:": "Payment instructions:",
  "Pago confirmado": "Payment confirmed",
  "Pago reportado — pendiente de confirmación": "Payment reported — pending confirmation",
  "Wire / transferencia": "Wire transfer",
  "Ya envié el pago": "I already sent the payment",
  "Pago reportado. Nuestro equipo lo confirmará.": "Payment reported. Our team will confirm it.",
  "Subasta perdida": "Auction lost",
  "Tu reembolso de {amount} fue procesado (cuota de procesamiento {fee}% = {feeAmount}). Puede tardar 5–10 días hábiles en reflejarse.":
    "Your refund of {amount} was processed ({fee}% processing fee = {feeAmount}). It may take 5–10 business days to appear.",
  "No ganamos esta vez. Tu depósito será reembolsado automáticamente a tu tarjeta menos la cuota de procesamiento divulgada.":
    "We didn't win this time. Your deposit will be automatically refunded to your card minus the disclosed processing fee.",
  "Servicios adicionales": "Additional services",
  "Reparación, transporte y exportación para este vehículo.": "Repair, transport and export for this vehicle.",
  "Solicitar servicio": "Request service",
  "Estimado de reparación": "Repair estimate",
  "Envío / transporte": "Shipping / transport",
  "Consultoría de exportación": "Export consulting",
  "Cuéntanos qué necesitas (ej. cotizar reparación de transmisión)":
    "Tell us what you need (e.g. quote a transmission repair)",
  "Enviar solicitud": "Send request",
  "Enviando...": "Sending...",
  "Solicitud de servicio registrada. Te contactaremos pronto.": "Service request logged. We'll contact you soon.",
  "WhatsApp directo": "Direct WhatsApp",
  "Historial": "History",
  "Hola CarBid, consulta sobre mi solicitud": "Hi CarBid, question about my request",

  // ---- Landing ----
  "Bróker licenciado de Copart": "Licensed Copart broker",
  "Enviar mi lote": "Submit my lot",
  "Hola CarBid, quiero información sobre una subasta de Copart": "Hi CarBid, I'd like information about a Copart auction",
  "Proceso transparente": "Transparent process",
  "Compra en subastas de": "Buy in",
  "sin ser miembro.": "auctions without being a member.",
  "Envíanos el lote, autoriza tu puja máxima y paga un depósito reembolsable. Nuestro equipo puja por ti y te acompaña con reparación, envío y exportación.":
    "Send us the lot, authorize your max bid and pay a refundable deposit. Our team bids for you and supports you with repair, shipping and export.",
  "1. Regístrate y sube tu ID": "1. Sign up and upload your ID",
  "Copart exige identificación (licencia o pasaporte). Súbela una vez desde tu perfil.":
    "Copart requires ID (driver's license or passport). Upload it once from your profile.",
  "2. Envía el lote y autoriza tu puja": "2. Submit the lot and authorize your bid",
  "Pega la URL o número de lote de Copart. Define tu puja máxima y acepta la comisión fija.":
    "Paste the Copart lot URL or number. Set your max bid and accept the flat fee.",
  "3. Paga el depósito reembolsable": "3. Pay the refundable deposit",
  "{pct}% de tu puja máxima (mín. {min}). Si no ganamos, se te devuelve.":
    "{pct}% of your max bid (min. {min}). If we don't win, you get it back.",
  "4. Pujamos por ti": "4. We bid for you",
  "Nuestro equipo puja manualmente en nuestra cuenta de Copart hasta tu máximo autorizado.":
    "Our team bids manually from our Copart account up to your authorized maximum.",
  "5. Gana, paga y recibe": "5. Win, pay and receive",
  "Ganamos: pagas el saldo por wire o Zelle. Perdemos: reembolso automático de tu depósito.":
    "We win: you pay the balance by wire or Zelle. We lose: automatic deposit refund.",
  "6. Servicios adicionales": "6. Additional services",
  "Estimados de reparación, transporte nacional y consultoría de exportación a tu país.":
    "Repair estimates, nationwide transport and export consulting to your country.",
  "Sin sorpresas": "No surprises",
  "Tarifas claras, por escrito": "Clear pricing, in writing",
  "Comisión de bróker: {fee}": "Broker fee: {fee}",
  " — tarifa plana por vehículo, solo si ganamos tu subasta.": " — flat fee per vehicle, only if we win your auction.",
  "Depósito: {pct}% de tu puja máxima (mín. {min})": "Deposit: {pct}% of your max bid (min. {min})",
  " — 100% reembolsable si no ganamos.": " — 100% refundable if we don't win.",
  "Reembolso:": "Refunds:",
  " se descuenta solo la cuota de procesamiento de tarjeta: {us}% tarjetas de EE.UU. / {foreign}% tarjetas extranjeras.":
    " only the card processing fee is deducted: {us}% US cards / {foreign}% foreign cards.",
  "Estimador de costos:": "Cost estimator:",
  " vehículo + comisión + impuestos estimados + transporte, antes de pujar.":
    " vehicle + fee + estimated taxes + transport, before you bid.",
  "Depósito reembolsable": "Refundable deposit",
  "Si no ganamos, te devolvemos tu dinero": "If we don't win, you get your money back",
  "Más que pujas": "More than bidding",
  "Talleres aliados evalúan tu vehículo salvage y te cotizan la reparación antes de que decidas.":
    "Partner shops assess your salvage vehicle and quote the repair before you decide.",
  "Coordinamos el transporte desde el patio de Copart hasta tu dirección o puerto.":
    "We coordinate transport from the Copart yard to your address or port.",
  "Documentación, título, aduana y contenedor. Te asesoramos de puerta a puerto.":
    "Paperwork, title, customs and container. We advise you door to door.",
  "Consultar por WhatsApp": "Ask on WhatsApp",
  "Hola CarBid, me interesa:": "Hi CarBid, I'm interested in:",
  "Preguntas frecuentes": "Frequently asked questions",
  "¿Necesito licencia de dealer para comprar en Copart?": "Do I need a dealer license to buy at Copart?",
  "No. CarBid puja con su propia cuenta licenciada de bróker, así que puedes comprar vehículos salvage y usados sin licencia propia.":
    "No. CarBid bids from its own licensed broker account, so you can buy salvage and used vehicles without your own license.",
  "¿Qué pasa con mi depósito si no ganan la subasta?": "What happens to my deposit if you don't win the auction?",
  "Te lo devolvemos automáticamente a tu tarjeta. Solo se descuenta la cuota de procesamiento que cobra la red de tarjetas (3.5% tarjetas de EE.UU. / 4.5% extranjeras), divulgada antes de pagar.":
    "We automatically refund it to your card. Only the card network processing fee is deducted (3.5% US cards / 4.5% foreign), disclosed before you pay.",
  "¿Cómo pago el saldo si gano?": "How do I pay the balance if I win?",
  "Por transferencia wire o Zelle. Nuestro equipo confirma la recepción y tu solicitud pasa a 'Pagado'. Desde ahí coordinamos título, transporte y exportación.":
    "By wire transfer or Zelle. Our team confirms receipt and your request moves to 'Paid'. From there we coordinate title, transport and export.",
  "¿Qué pasa si la página de Copart no carga los datos del lote?": "What if the Copart page doesn't load the lot details?",
  "Nada se rompe: el auto-rellenado es solo una conveniencia. Siempre puedes ingresar los datos del vehículo manualmente y nuestro equipo puede corregirlos después.":
    "Nothing breaks: auto-fill is just a convenience. You can always enter the vehicle details manually and our team can correct them later.",
  "¿Puedo pujar por más de un vehículo?": "Can I bid on more than one vehicle?",
  "Sí. Cada lote se rastrea de forma independiente con su propio depósito, estado e historial.":
    "Yes. Each lot is tracked independently with its own deposit, status and history.",
  "¿Listo para pujar?": "Ready to bid?",
  "Crea tu cuenta en 2 minutos, sube tu ID y envíanos tu primer lote de Copart.":
    "Create your account in 2 minutes, upload your ID and send us your first Copart lot.",
  "Crear cuenta gratis": "Create free account",
  "Inventario de subastas": "Auction inventory",
  "Vehículos premium que nuestros clientes ganan en Copart": "Premium vehicles our clients win at Copart",
  "SUVs y camionetas de lujo a precios muy por debajo del mercado. Ejemplos del inventario típico de las subastas.":
    "Luxury SUVs and trucks at prices well below market. Examples of typical auction inventory.",
  "SUV de lujo": "Luxury SUV",
  "Deportivo premium": "Premium sports",
  "El favorito para exportar": "The export favorite",
  "Pujar ahora": "Bid now",
  "Imágenes referenciales del tipo de vehículos disponibles en subastas Copart.":
    "Reference images of the type of vehicles available at Copart auctions.",
  "¿Dudas? Habla con un bróker": "Questions? Talk to a broker",
  "Escríbenos por WhatsApp": "Message us on WhatsApp",
  "Hola CarBid, tengo una consulta": "Hi CarBid, I have a question",
  "Depósito reembolsable ({pct}% · mín. {min})": "Refundable deposit ({pct}% · min. {min})",
  "Si no ganamos la subasta, tu depósito se reembolsa automáticamente menos la cuota de procesamiento de tarjeta ({us}% tarjetas de EE.UU. / {foreign}% tarjetas extranjeras). Si ganamos y no pagas el saldo en el plazo indicado, el depósito puede ser retenido como penalidad.":
    "If we don't win the auction, your deposit is automatically refunded minus the card processing fee ({us}% US cards / {foreign}% foreign cards). If we win and you don't pay the balance within the stated deadline, the deposit may be retained as a penalty.",
  "Acepto la comisión de bróker de {fee}, la política de reembolso del depósito y autorizo a CarBid a pujar hasta mi monto máximo en su cuenta de Copart.":
    "I accept the {fee} broker fee, the deposit refund policy, and authorize CarBid to bid up to my maximum amount from its Copart account.",
  "No pudimos obtener los datos automáticamente (Copart bloquea el acceso). Complétalos manualmente abajo.":
    "We couldn't fetch the details automatically (Copart blocks access). Fill them in manually below.",
};

const LangContext = createContext(null);

function translate(lang, key, vars) {
  let s = lang === "en" ? (EN[key] ?? key) : key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

export const useLang = () => useContext(LangContext);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("carbid_lang") || "es");
  const setLang = useCallback((l) => {
    localStorage.setItem("carbid_lang", l);
    setLangState(l);
  }, []);
  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function LanguageToggle() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex items-center border border-white/15 rounded-full overflow-hidden text-xs font-bold">
      <button data-testid="lang-es" onClick={() => setLang("es")}
        className={`px-2.5 py-1 transition-colors ${lang === "es" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}>
        ES
      </button>
      <button data-testid="lang-en" onClick={() => setLang("en")}
        className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}>
        EN
      </button>
    </div>
  );
}
